import { mockDocument } from 'src/__mocks__/documents/documentMock';
import { createHttpService } from '../ApiService';
import {
  TCreateDocumentRequest,
  TDocument,
  TDocumentDetails,
  TDocumentOwnerType,
  TDocumentResponse,
} from './documents.interfaces';
import { TPaginationParams } from '../__shared/shared.interfaces';
import { mockDocumentDetails } from 'src/__mocks__/documents/documentDetailsMock';
import { mockStudentDocuments } from '../../__mocks__/students/studentDocumentsMock';
import { mockStaffDocuments } from '../../__mocks__/staff/staffDocumentsMock';
import { localStorageGet } from 'src/utils/localStorage';
import isAppWithMock from 'src/utils/isAppWithMock';

type TCreateDocumentPayload = TCreateDocumentRequest & { file?: File };

type TLoggedInUserDocumentContext = {
  id?: string | null;
  instituteId?: string;
};

type TDocumentTokenPayload = {
  _id?: string;
  institute_ref?: string;
};

const decodeTokenPayload = (): TDocumentTokenPayload => {
  const token = localStorageGet('token', '') as string;
  const encodedPayload = token.split('.')[1];

  if (!encodedPayload) {
    return {};
  }

  try {
    const payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload.padEnd(
      payload.length + ((4 - (payload.length % 4)) % 4),
      '='
    );
    return JSON.parse(window.atob(paddedPayload)) as TDocumentTokenPayload;
  } catch (error) {
    console.warn('Unable to decode document upload token payload', error);
    return {};
  }
};

const getDocumentUserContext = () => {
  const currentUser = localStorageGet('loggedInUser', undefined) as
    | TLoggedInUserDocumentContext
    | undefined;
  const tokenPayload = decodeTokenPayload();

  return {
    userId: currentUser?.id ?? tokenPayload._id ?? '',
    instituteId: currentUser?.instituteId ?? tokenPayload.institute_ref ?? '',
  };
};

const normalizeCreateDocumentRequest = (
  request: TCreateDocumentPayload
): TCreateDocumentPayload => {
  const context = getDocumentUserContext();
  const normalizedRequest = {
    ...request,
    ownerId: request.ownerId || context.userId,
    createdBy: request.createdBy || context.userId,
    instituteId: request.instituteId || context.instituteId,
  };

  if (
    !normalizedRequest.ownerId ||
    !normalizedRequest.createdBy ||
    !normalizedRequest.instituteId ||
    !normalizedRequest.verificationDate ||
    !normalizedRequest.file
  ) {
    console.warn('Document upload payload missing required fields', {
      hasOwnerId: Boolean(normalizedRequest.ownerId),
      hasCreatedBy: Boolean(normalizedRequest.createdBy),
      hasInstituteId: Boolean(normalizedRequest.instituteId),
      hasVerificationDate: Boolean(normalizedRequest.verificationDate),
      hasFile: Boolean(normalizedRequest.file),
    });
  }

  return normalizedRequest;
};

const buildCreateDocumentFormData = (request: TCreateDocumentPayload) => {
  const formData = new FormData();

  formData.append('name', request.name);
  formData.append('description', request.description ?? request.name);
  formData.append('ownerId', request.ownerId);
  formData.append('ownerType', request.ownerType);
  formData.append('createdBy', request.createdBy);

  if (request.instituteId) {
    formData.append('instituteId', request.instituteId);
  }

  if (request.verificationDate) {
    formData.append('verificationDate', request.verificationDate);
  }

  if (request.isVerifiable !== undefined) {
    formData.append('isVerifiable', String(request.isVerifiable));
  }

  if (request.file) {
    formData.append('file', request.file, request.file.name);
  }

  return formData;
};

const responseIsoDatePrefix = (value: string | undefined): string => {
  if (typeof value !== 'string' || value.length < 10) return '';
  return value.slice(0, 10);
};

const mapResponseToDocument = (doc: TDocumentResponse): TDocument => {
  const createdTs = Date.parse(doc.createdAt);
  const uploadedTimestamp = Number.isFinite(createdTs)
    ? createdTs
    : Date.now();

  const verifiedPrefix = responseIsoDatePrefix(doc.verifiedAt);

  return {
    documentId: doc.id,
    userId: doc.ownerId,
    documentType: doc.documentType as unknown as TDocument['documentType'],
    documentName: doc.name,
    submissionDate:
      responseIsoDatePrefix(doc.createdAt) ||
      new Date().toISOString().slice(0, 10),
    verificationDate: verifiedPrefix || undefined,
    status: doc.status,
    remarks: doc.verificationNotes ?? doc.rejectionReason,
    fileUrl: doc.signedUrl,
    thumbnailUrl: doc.thumbnailSignedUrl,
    uploadedAt: {
      userId: doc.verifiedBy ?? doc.ownerId ?? '',
      timestamp: uploadedTimestamp,
    },
  };
};

export const useDocumentService = () => {
  const httpAPI = createHttpService();
  const isMockData = isAppWithMock();

  const getDocuments = (params: TPaginationParams): Promise<TDocument[]> => {
    if (isMockData) {
      return Promise.resolve([mockDocument]);
    }
    return httpAPI.documents.get<TDocument[]>('', { params });
  };

  const getDocument = (documentId: string): Promise<TDocumentDetails> => {
    if (isMockData) {
      return Promise.resolve(mockDocumentDetails);
    }
    return httpAPI.documents.get<TDocumentDetails>(`/${documentId}`);
  };

  const getStudentDocuments = (
    params: TPaginationParams
  ): Promise<TDocument[]> => {
    if (isMockData) {
      return Promise.resolve(mockStudentDocuments);
    }
    return httpAPI.documents
      .get<TDocumentResponse[]>('/students', { params })
      .then((docs) => docs.map(mapResponseToDocument));
  };

  const getStaffDocuments = (
    params: TPaginationParams
  ): Promise<TDocument[]> => {
    if (isMockData) {
      return Promise.resolve(mockStaffDocuments);
    }
    return httpAPI.documents.get<TDocument[]>('/staff', { params });
  };

  const createDocument = (
    request: TCreateDocumentPayload
  ): Promise<TDocumentResponse> => {
    if (isMockData) {
      return Promise.resolve(mockDocument as unknown as TDocumentResponse);
    }

    return httpAPI.documents.post<TDocumentResponse>(
      '',
      buildCreateDocumentFormData(normalizeCreateDocumentRequest(request))
    );
  };

  const uploadFile = (
    file: File,
    metadata?: Partial<TCreateDocumentRequest>
  ): Promise<TDocument> => {
    if (isMockData) {
      return Promise.resolve(mockDocument);
    }
    const context = getDocumentUserContext();
    const createdBy = metadata?.createdBy ?? context.userId;
    const ownerId = metadata?.ownerId ?? createdBy;

    const formData = buildCreateDocumentFormData(
      normalizeCreateDocumentRequest({
        name: metadata?.name ?? file.name,
        description: metadata?.description ?? file.name,
        ownerType: metadata?.ownerType ?? TDocumentOwnerType.USER,
        ownerId,
        instituteId: metadata?.instituteId ?? context.instituteId,
        createdBy,
        verificationDate:
          metadata?.verificationDate ?? new Date().toISOString(),
        isVerifiable: metadata?.isVerifiable ?? true,
        file,
      })
    );

    return httpAPI.documents
      .post<TDocumentResponse>('', formData)
      .then(mapResponseToDocument);
  };

  const updateDocument = (
    documentId: string,
    file: File
  ): Promise<TDocument> => {
    if (isMockData) {
      return Promise.resolve(mockDocument);
    }
    return httpAPI.documents.put<TDocument>('', { documentId, file });
  };

  const deleteDocument = (documentId: string): Promise<void> => {
    if (isMockData) {
      return Promise.resolve();
    }
    return httpAPI.documents.delete<void>(`/${documentId}`);
  };

  return {
    getDocuments,
    getDocument,
    getStudentDocuments,
    getStaffDocuments,
    createDocument,
    uploadFile,
    updateDocument,
    deleteDocument,
  };
};
